import React, { useEffect, useState } from "react";
import { useLocation, useSearchParams, Navigate } from "react-router-dom";
import CategoryTabs from "../components/CategoryTabs";
import PageLayout from "../components/layout/HomePageLayout";
import SEOHead from "@/components/seo/SEOHead";
import Hero from "../components/hero/Hero";
import DesignerGrid from "../components/DesignerGrid";
import PortfolioGrid from "../components/PortfolioGrid";
import { ChevronUp } from "lucide-react";

const Index = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'designers' | 'portfolios'>('portfolios');
  const [currentPage, setCurrentPage] = useState(1);
  const [showScrollToTop, setShowScrollToTop] = useState(false);

  useEffect(() => {
    // Check if we need to refresh the page
    if (location.state?.refresh) {
      // Clear the state to prevent infinite refresh
      window.history.replaceState({}, document.title);
      // Trigger a re-render of the components
      window.dispatchEvent(new Event('refresh'));
    }
  }, [location]);

  // Handle scroll to show/hide scroll to top button
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setShowScrollToTop(scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle tab changes from URL params
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'portfolios' || tabParam === 'designers') {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  const handleTabChange = (tab: 'designers' | 'portfolios') => {
    setActiveTab(tab);
    
    // Update URL params
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', tab);
    newParams.delete('page'); // Reset pagination when switching tabs
    setSearchParams(newParams, { replace: true });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const isFirstPage = currentPage === 1;

  // Only redirect if accessed from root path, not from /discover
  if (location.pathname === '/') {
    return <Navigate to="/" replace />;
  }

  return (
    <PageLayout>
      <SEOHead
        title="Designer directory"
        description="The designers Guidione reads from when founders hire through World Class Design. Screened by hand, not a self-serve marketplace."
      />

      <div className="relative z-30 bg-white">
        {/* Warm gradient background applied to the content area */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute warm-gradient"></div>
          <div
            className="absolute inset-0 opacity-40"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3z' fill='%23FFDFCE' fill-opacity='0.05' fill-rule='evenodd'/%3E%3C/svg%3E\")",
              opacity: "0.4",
            }}
          ></div>
        </div>

        {/* Hero Section */}
        {isFirstPage && <Hero />}
        
        <div className={`container mx-auto px-[4px] ${!isFirstPage ? 'pt-8' : ''}`}>
          <div className="mb-4">
            <CategoryTabs />
          </div>

          <div className="lg:hidden"></div>
          <div className="flex flex-col gap-6 px-4 md:px-0">
            <div
              className="w-full animate-slide-in-right"
              style={{ animationDelay: "0.2s" }}
            >
              {activeTab === 'designers' ? (
                <DesignerGrid />
              ) : (
                <PortfolioGrid showFilters={false} onPageChange={handlePageChange} />
              )}
            </div>
          </div>
        </div>

        {/* Smooth Scroll to Top Button */}
        {showScrollToTop && (
          <button
            onClick={handleScrollToTop}
            className="fixed bottom-8 right-8 z-50 bg-[#231617] text-white p-3 rounded-full shadow-lg hover:bg-[#3a2327] transition-all duration-300 transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-[#231617] focus:ring-offset-2"
            aria-label="Scroll to top"
          >
            <ChevronUp className="w-6 h-6" />
          </button>
        )}
      </div>
    </PageLayout>
  );
};

export default Index;
